import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, ClipboardCheck, AlertCircle, ShieldOff, ListOrdered } from "lucide-react";
import { bookConditionsApi, type CreateBookConditionDto } from "@/lib/api/book-conditions";
import { BOOK_CONDITIONS_QUERY_KEY } from "@/hooks/useBookConditions";
import { cn } from "@/lib/utils";

const BookConditionFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateBookConditionDto>({
    code: "",
    label: "",
    description: "",
    order: 0,
    isActive: true,
  });

  const { isTenantAdmin } = useAccessControl();
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadBookCondition(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  const loadBookCondition = async (conditionId: string) => {
    try {
      setLoading(true);
      const condition = await bookConditionsApi.getById(conditionId);
      setFormData({
        code: condition.code,
        label: condition.label,
        description: condition.description || "",
        order: condition.order,
        isActive: condition.isActive,
      });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load condition.");
      navigate("/setup/book-conditions");
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "code":
        if (!value || !value.trim()) return "Code is required";
        if (!/^[A-Z0-9_]+$/.test(value))
          return "Use only uppercase letters, numbers, and underscores";
        return "";
      case "label":
        if (!value || !value.trim()) return "Label is required";
        if (value.length < 2) return "Label must be at least 2 characters";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    newErrors.code = validateField("code", formData.code);
    newErrors.label = validateField("label", formData.label);

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );

    setErrors(filteredErrors);
    setTouched(new Set(["code", "label"]));

    return Object.keys(filteredErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    const value = formData[field as keyof typeof formData];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        description: formData.description || undefined,
      };

      if (isEditing && id) {
        await bookConditionsApi.update(id, submitData);
        toast.success(`"${formData.label}" has been updated successfully.`);
      } else {
        await bookConditionsApi.create(submitData);
        toast.success(`"${formData.label}" has been created successfully.`);
      }

      queryClient.invalidateQueries(BOOK_CONDITIONS_QUERY_KEY);
      setHasUnsavedChanges(false);
      navigate("/setup/book-conditions");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation("/setup/book-conditions");
      setShowDiscardDialog(true);
    } else {
      navigate("/setup/book-conditions");
    }
  };

  const handleDiscardConfirm = () => {
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const getFieldStatus = (field: string) => {
    if (!touched.has(field)) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage book conditions.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading condition..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <div className="mb-6">
          <PageHeader
            title={isEditing ? "Edit Book Condition" : "Add Book Condition"}
            description={
              isEditing
                ? "Update the details for this condition"
                : "Create a new physical condition label for library materials"
            }
            breadcrumbItems={[
              { label: "Setup", href: "/setup" },
              { label: "Book Conditions", href: "/setup/book-conditions" },
              { label: isEditing ? "Edit" : "New" },
            ]}
            showSearch={false}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-primary/10">
                <ClipboardCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Condition Details</h3>
                <p className="text-sm text-gray-500">Define the condition code and label</p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label htmlFor="code" className="flex items-center gap-1 mb-2">
                    Condition Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    onBlur={() => handleFieldBlur("code")}
                    placeholder="e.g., GOOD, DAMAGED, POOR"
                    className={cn(
                      getFieldStatus("code") === "error" && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {touched.has("code") && errors.code && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.code}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    Uppercase letters, numbers, and underscores only
                  </p>
                </div>

                <div>
                  <Label htmlFor="label" className="flex items-center gap-1 mb-2">
                    Display Label <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    onBlur={() => handleFieldBlur("label")}
                    placeholder="e.g., Good Condition, Damaged, Poor"
                    className={cn(
                      getFieldStatus("label") === "error" && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {touched.has("label") && errors.label && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.label}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    This is what staff will see when selecting conditions
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="flex items-center gap-1 mb-2">
                  Description
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Briefly describe what this condition means (e.g., 'Minor wear, fully functional')"
                  rows={2}
                />
              </div>
            </div>
          </Card>

          {/* Display Order */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-teal-100">
                <ListOrdered className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Display Order</h3>
                <p className="text-sm text-gray-500">
                  Control where this condition appears in lists
                </p>
              </div>
            </div>

            <div className="max-w-xs">
              <Label htmlFor="order" className="mb-2">
                Sort Order
              </Label>
              <Input
                id="order"
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
              <p className="mt-1.5 text-xs text-gray-500">
                Lower numbers appear first in dropdown menus. Use 0 for the top position.
              </p>
            </div>
          </Card>

          {/* Status */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked === true })
                }
              />
              <div className="flex-1">
                <Label htmlFor="isActive" className="font-medium cursor-pointer">
                  Active
                </Label>
                <p className="text-xs text-gray-500">
                  Inactive conditions won't appear in selection dropdowns
                </p>
              </div>
              <span
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium",
                  formData.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                )}
              >
                {formData.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Condition"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      {/* Discard Changes Dialog */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard unsaved changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscardConfirm}
        variant="warning"
      />
    </AppLayout>
  );
};

export default BookConditionFormPage;
